import { createContext, useContext, useState, useEffect } from "react";
import {
  getSessionStorage,
  removeSessionStorage,
  setSessionStorage,
} from "util/storageUtil";
import { setOATState } from "commons/util/oatBridge";
import {
  getAppConfig,
  updateProtectedApis,
} from "commons/util/appConfigHelper";

const OATContext = createContext(null);

const getInitialOATState = () => {
  try {
    const stored = getSessionStorage("OAT_STORE_DETAIL");

    if (!stored) {
      return {
        isOAT: false,
        OATConfig: {},
        OATStoreDetail: {},
        OATDonation: {},
        OATMenuType: "",
        waypointId: "",
      };
    }

    const parsed = JSON.parse(stored);

    return {
      isOAT: parsed?.storeConfig?.type === "OAT",
      OATConfig: parsed?.storeConfig || {},
      OATStoreDetail: parsed || {},
      OATMenuType: parsed?.storeConfig?.menuType,
      OATDonation: {
        enabled: Boolean(parsed?.donation?.enabled),
        donationOptions: Array.isArray(parsed?.donation?.options)
          ? parsed.donation.options
          : [],
      },
      waypointId: parsed?.waypointId,
    };
  } catch (error) {
    console.error("Failed to hydrate OAT context", error);
    return {
      isOAT: false,
      OATConfig: {},
      OATStoreDetail: {},
      OATDonation: {
        enabled: false,
        donationOptions: [],
      },
      OATMenuType: "",
      waypointId: "",
    };
  }
};

export const OATProvider = ({ children, setLanguageList }) => {
  const [state, setState] = useState(() => getInitialOATState());

  useEffect(() => {
    setOATState({
      isOAT: state.isOAT,
      OATConfig: state.OATConfig,
    });
  }, [state.isOAT, state.OATConfig]);

  const setOATConfig = (config) => {
    setState((prev) => ({
      ...prev,
      isOAT: config?.type === "OAT",
      OATConfig: config,
    }));
  };

  const setOATStoreDetail = (detail) => {
    setSessionStorage("OAT_STORE_DETAIL", JSON.stringify(detail));
    const OATEnabled = detail?.storeConfig?.type === "OAT";
    const secondaryOATGatewayUrl = getAppConfig("oatGatewayURL");

    setState((prev) => ({
      ...prev,
      OATStoreDetail: detail,
      OATConfig: detail?.storeConfig || {},
      isOAT: OATEnabled,
      OATMenuType: detail?.storeConfig?.menuType,
      OATDonation: {
        enabled: detail?.donation?.enabled || false,
        donationOptions: Array.isArray(detail?.donation?.options)
          ? detail?.donation?.options
          : [],
      },
      waypointId: detail?.waypointId,
    }));
    setLanguageList(detail?.storeConfig?.multiLangOption);
    if (OATEnabled && secondaryOATGatewayUrl) {
      updateProtectedApis(secondaryOATGatewayUrl);
    }
  };

  const setOATDonation = (donation) => {
    setState((prev) => ({
      ...prev,
      OATDonation: donation,
    }));
  };

  const resetOAT = () => {
    removeSessionStorage("OAT_STORE_DETAIL");
    removeSessionStorage("IsOATTermsAndConditionsAccepted");
    setState({
      isOAT: false,
      OATConfig: {},
      OATStoreDetail: {},
      OATDonation: {},
      OATMenuType: "",
    });
  };

  return (
    <OATContext.Provider
      value={{
        ...state,
        setOATConfig,
        setOATStoreDetail,
        setOATDonation,
        resetOAT,
      }}
    >
      {children}
    </OATContext.Provider>
  );
};

export const useOAT = () => {
  const context = useContext(OATContext);
  if (!context) {
    throw new Error("useOAT must be used within an OATProvider");
  }
  return context;
};
